import Head from "next/head";
import Router from "next/router";
import nProgress from "nprogress";
import "nprogress/nprogress.css";
import ThemeProvider from "src/theme/ThemeProvider";
import CssBaseline from "@mui/material/CssBaseline";
import { CacheProvider } from "@emotion/react";
import createEmotionCache from "src/createEmotionCache";
import { appWithTranslation } from "next-i18next";
import { SidebarProvider } from "src/contexts/SidebarContext";
import Loader from "src/components/Loader";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers";
import useScrollTop from "src/hooks/useScrollTop";
import { SnackbarProvider } from "notistack";
import {
  AuthConsumer,
  AuthProvider,
  AuthContext,
} from "src/contexts/FirebaseAuthContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  FpjsProvider,
  FingerprintJSPro,
  CacheLocation,
} from "@fingerprintjs/fingerprintjs-pro-react";
import { useEffect, useContext } from "react";
import { useRouter } from "next/router";
import Intercom from "@intercom/messenger-js-sdk";
import { SessionProvider } from "next-auth/react";

const clientSideEmotionCache = createEmotionCache();

// Component to handle Intercom URL search parameter - must be inside AuthProvider
const IntercomUrlHandler = () => {
  const router = useRouter();
  const { user } = useContext(AuthContext);

  useEffect(() => {
    if (router.isReady) {
      const searchParams = new URLSearchParams(
        router.asPath.split("?")[1] || ""
      );
      const isIntercom = searchParams.get("chat");

      if (isIntercom) {
        try {
          if (user) {

            Intercom({
              app_id: "l2erqbk4",
              user_id: user.public_id.toString(),
              name: user.agent_name || user.owner_info.name,
              company: "POS",
              created_at: user.createdAt
                ? Math.floor(new Date(user.createdAt).getTime() / 1000)
                : undefined,
              hide_default_launcher: true,
            });
          } else {
            // Unauthenticated user
            Intercom({
              app_id: "l2erqbk4",
              company: "POS",
              hide_default_launcher: true,
            });
          }

          // Show Intercom messenger
          if (typeof window !== "undefined" && window.Intercom) {
            window.Intercom("show");
          }
        } catch (error) {
          console.error("Error initializing Intercom:", error);
          // Fallback - initialize without user data if there's an error
          Intercom({
            app_id: "l2erqbk4",
            company: "POS",
            hide_default_launcher: true,
          });
          if (typeof window !== "undefined" && window.Intercom) {
            window.Intercom("show");
          }
        }
      }
    }
  }, [router.isReady, router.asPath, user]);

  return null; // This component doesn't render anything
};

function POSApp(props) {
  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props;

  const getLayout = Component.getLayout ?? ((page) => page);
  const queryClient = new QueryClient();
  const fpjsPublicApiKey = process.env.NEXT_PUBLIC_FINGERPRINTJS_API_KEY;

  useScrollTop();

  Router.events.on("routeChangeStart", nProgress.start);
  Router.events.on("routeChangeError", nProgress.done);
  Router.events.on("routeChangeComplete", nProgress.done);

  return (
    <CacheProvider value={emotionCache}>
      <Head>
        <title>POS Hondubet</title>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, shrink-to-fit=no"
        />
        <meta name="google" content="notranslate" />
      </Head>
      <SessionProvider session={pageProps.session}>
        <FpjsProvider
          loadOptions={{
            apiKey: fpjsPublicApiKey,
            endpoint: [
              "https://fp.hondubet.com",
              FingerprintJSPro.defaultEndpoint,
            ],
            scriptUrlPattern: [
              "https://fp.hondubet.com/web/v<version>/<apiKey>/loader_v<loaderVersion>.js",
              FingerprintJSPro.defaultScriptUrlPattern,
            ],
          }}
          cacheLocation={CacheLocation.SessionStorage}
          cacheTimeInSeconds={3600}
        >
          <QueryClientProvider client={queryClient}>
            <SidebarProvider>
              <ThemeProvider>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <AuthProvider>
                    <SnackbarProvider
                      maxSnack={6}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                      }}
                    >
                      <CssBaseline />
                      <AuthConsumer>
                        {(auth) => (
                          <>
                            <IntercomUrlHandler />
                            {!auth.isInitialized ? (
                              <Loader />
                            ) : (
                              getLayout(<Component {...pageProps} />)
                            )}
                          </>
                        )}
                      </AuthConsumer>
                    </SnackbarProvider>
                  </AuthProvider>
                </LocalizationProvider>
              </ThemeProvider>
            </SidebarProvider>
          </QueryClientProvider>
        </FpjsProvider>
      </SessionProvider>
    </CacheProvider>
  );
}

export default appWithTranslation(POSApp);
